// @ts-nocheck
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ExternalLink, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface StripeConnectOnboardingProps {
  affiliate: {
    stripe_account_id?: string;
    stripe_account_status?: string;
    tax_info_status?: string;
    payout_method?: string;
  };
  onAccountConnected?: () => void;
}

export default function StripeConnectOnboarding({ 
  affiliate, 
  onAccountConnected 
}: StripeConnectOnboardingProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  const handleConnectAccount = async () => {
    try {
      setIsConnecting(true);
      
      const { data, error } = await invokeEdgeFunction('stripe-connect-onboarding', {
        body: { action: 'create_account' }
      });

      if (error) throw error;

      // Redirect to Stripe onboarding
      window.location.href = data.onboarding_url;
      
    } catch (error) {
      console.error('Connect account error:', error);
      toast.error('Failed to connect Stripe account');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!affiliate.stripe_account_id) return;

    try {
      setIsCheckingStatus(true);
      
      const { data, error } = await invokeEdgeFunction('stripe-connect-onboarding', {
        body: { 
          action: 'get_account_status', 
          account_id: affiliate.stripe_account_id 
        }
      });

      if (error) throw error;

      toast.success('Account status updated');
      onAccountConnected?.();
      
    } catch (error) {
      console.error('Check status error:', error);
      toast.error('Failed to check account status');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleManageAccount = async () => {
    if (!affiliate.stripe_account_id) return;

    try {
      const { data, error } = await invokeEdgeFunction('stripe-connect-onboarding', {
        body: { 
          action: 'create_login_link', 
          account_id: affiliate.stripe_account_id 
        }
      });

      if (error) throw error;

      window.open(data.url, '_blank');
      
    } catch (error) {
      console.error('Manage account error:', error);
      toast.error('Failed to access account dashboard');
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-success text-success-foreground">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </Badge>;
      case 'restricted':
        return <Badge variant="destructive">
          <AlertCircle className="w-3 h-3 mr-1" />
          Restricted
        </Badge>;
      case 'pending':
      default:
        return <Badge variant="secondary">
          <AlertCircle className="w-3 h-3 mr-1" />
          Pending
        </Badge>;
    }
  };

  const getTaxStatusBadge = (status?: string) => {
    switch (status) {
      case 'verified':
        return <Badge variant="default" className="bg-success text-success-foreground">
          <CheckCircle className="w-3 h-3 mr-1" />
          Verified
        </Badge>;
      case 'submitted':
        return <Badge variant="secondary">
          <AlertCircle className="w-3 h-3 mr-1" />
          Under Review
        </Badge>;
      case 'pending':
      default:
        return <Badge variant="outline">
          <AlertCircle className="w-3 h-3 mr-1" />
          Required
        </Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Payout Account Setup
        </CardTitle>
        <CardDescription>
          Connect your Stripe account to receive automated payouts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!affiliate.stripe_account_id ? (
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">Benefits of Automated Payouts:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Automatic monthly payments</li>
                <li>• Secure bank transfers</li>
                <li>• Real-time payout tracking</li>
                <li>• Tax document generation</li>
              </ul>
            </div>
            
            <Button 
              onClick={handleConnectAccount}
              disabled={isConnecting}
              className="w-full"
            >
              {isConnecting ? 'Connecting...' : 'Connect Payout Account'}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Account Status:</span>
              {getStatusBadge(affiliate.stripe_account_status)}
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Tax Info Status:</span>
              {getTaxStatusBadge(affiliate.tax_info_status)}
            </div>

            {affiliate.stripe_account_status === 'pending' && (
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <p className="text-sm text-warning-foreground">
                  Your account setup is incomplete. Please complete the onboarding process to receive payouts.
                </p>
              </div>
            )}

            {affiliate.stripe_account_status === 'restricted' && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive-foreground">
                  Your account has restrictions. Please review and update your information.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCheckStatus}
                disabled={isCheckingStatus}
                className="flex-1"
              >
                {isCheckingStatus ? 'Checking...' : 'Refresh Status'}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleManageAccount}
                className="flex-1"
              >
                Manage Account
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}